/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

import { Queue } from "../utils/Queue";
import type { MouseState } from "./MouseState";

export class ScrollState {
  private readonly deltaQueue: Queue<number> = new Queue<number>();
  private isTrackpadScrollAllowed = true;
  private shouldUseThreshold = true;
  private minDelta = 10000;
  private readonly thresholdDelay = 100;
  private readonly maxMagnitude = 10;

  handleScrollEvent(
    event: WheelEvent,
    mouseState: MouseState,
    sendMessage: (message: string) => void,
  ): void {
    const deltaY = Math.trunc(Math.abs(event.deltaY));

    // Detect input device type
    this.updateDeviceDetection(deltaY);

    // Apply throttling for trackpad
    if (this.shouldUseThreshold && this.isTrackpadScrollAllowed) {
      this.processScrollEvent(event, mouseState, sendMessage);
      this.throttleTrackpadScroll();
    } else if (!this.shouldUseThreshold) {
      this.processScrollEvent(event, mouseState, sendMessage);
    }
  }

  private updateDeviceDetection(deltaY: number): void {
    if (this.deltaQueue.size() < 4) {
      this.deltaQueue.enqueue(deltaY);
      return;
    }

    if (this.deltaQueue.size() === 4) {
      this.shouldUseThreshold = !this.detectMouseDevice();
    }
  }

  private detectMouseDevice(): boolean {
    let consecutiveMatches = 0;
    let previousValue = this.deltaQueue.dequeue();

    while (!this.deltaQueue.isEmpty()) {
      const currentValue = this.deltaQueue.dequeue();
      if (currentValue >= 80 && previousValue === currentValue) {
        consecutiveMatches++;
      }
      previousValue = currentValue;
    }

    return consecutiveMatches >= 2;
  }

  private processScrollEvent(
    event: WheelEvent,
    mouseState: MouseState,
    sendMessage: (message: string) => void,
  ): void {
    const messageType = document.pointerLockElement ? "m2" : "m";
    const isScrollUp = event.deltaY < 0;
    const scrollButton = isScrollUp ? 4 : 3;

    let deltaY = Math.abs(Math.trunc(event.deltaY));

    // Track minimum delta for normalization
    if (deltaY > 0 && deltaY < this.minDelta) {
      this.minDelta = deltaY;
    }

    // Normalize and limit magnitude
    deltaY = Math.floor(deltaY / this.minDelta);
    const magnitude = Math.min(deltaY, this.maxMagnitude);

    // Send button press and release
    this.sendScrollMessages(
      messageType,
      scrollButton,
      magnitude,
      mouseState,
      sendMessage,
    );
    event.preventDefault();
  }

  private sendScrollMessages(
    messageType: string,
    button: number,
    magnitude: number,
    mouseState: MouseState,
    sendMessage: (message: string) => void,
  ): void {
    const buttonBit = 1 << button;

    // Press button
    const pressedMask = mouseState.buttonMask | buttonBit;
    sendMessage(
      `${messageType},${mouseState.x},${mouseState.y},${pressedMask},${magnitude}`,
    );

    // Release button
    const releasedMask = mouseState.buttonMask & ~buttonBit;
    sendMessage(
      `${messageType},${mouseState.x},${mouseState.y},${releasedMask},${magnitude}`,
    );
  }

  private throttleTrackpadScroll(): void {
    this.isTrackpadScrollAllowed = false;
    setTimeout(() => {
      this.isTrackpadScrollAllowed = true;
    }, this.thresholdDelay);
  }
}
